import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DatePipe } from '@angular/common';
import { ApiService } from '../../core/services/api.service';
import {
  SubscriptionPlan,
  SubscriptionPlanBenefit,
  UserSubscription,
} from '../../core/models/api.models';

type BenefitFormGroup = FormGroup<{
  key: FormControl<string>;
  icon_url: FormControl<string>;
  title: FormControl<string>;
  description: FormControl<string>;
  is_clickable: FormControl<boolean>;
  detail_url: FormControl<string>;
  sort_order: FormControl<number>;
}>;

@Component({
  selector: 'app-subscriptions',
  standalone: true,
  imports: [ReactiveFormsModule, DatePipe],
  templateUrl: './subscriptions.component.html',
  styleUrl: './subscriptions.component.scss',
})
export class SubscriptionsComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);

  readonly tab = signal<'plans' | 'users'>('plans');
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);

  readonly plans = signal<SubscriptionPlan[]>([]);
  readonly editingPlanId = signal<number | null>(null);

  readonly subs = signal<UserSubscription[]>([]);
  readonly page = signal(1);
  readonly total = signal(0);
  readonly limit = 20;
  readonly showGrant = signal(false);

  readonly planForm = this.fb.nonNullable.group({
    name: ['', Validators.required],
    description: [''],
    tagline: [''],
    card_label: ['MEMBERSHIP'],
    card_title: [''],
    card_image_url: [''],
    price: [0, Validators.required],
    currency: ['AUD'],
    currency_symbol: ['$'],
    billing_interval: [''],
    billing_label: ['/ month'],
    stripe_product_id: [''],
    stripe_price_id: [''],
    reward_multiplier: [1, Validators.required],
    rent_points: [null as number | null],
    member_offers: [false],
    ai_assistant: [false],
    smart_insights: [false],
    priority_support: [false],
    concierge_support: [false],
    airport_lounge_access: [false],
    travel_perks: [false],
    comparison_link_text: ['Compare all benefits'],
    primary_button_text: ['Continue'],
    is_active: [true],
    sort_order: [0],
    benefits: this.fb.array<BenefitFormGroup>([]),
  });

  readonly grantForm = this.fb.nonNullable.group({
    user_id: [1, Validators.required],
    plan_key: ['plus', Validators.required],
  });

  get benefits(): FormArray<BenefitFormGroup> {
    return this.planForm.controls.benefits;
  }

  createBenefitGroup(row?: Partial<SubscriptionPlanBenefit> | null): BenefitFormGroup {
    const nextOrder =
      row?.sort_order != null ? Number(row.sort_order) : this.benefits.length + 1;
    return this.fb.nonNullable.group({
      key: [row?.key || ''],
      icon_url: [row?.icon_url || ''],
      title: [row?.title || '', Validators.required],
      description: [row?.description || ''],
      is_clickable: [row?.is_clickable !== false],
      detail_url: [row?.detail_url || ''],
      sort_order: [nextOrder],
    });
  }

  ngOnInit(): void {
    this.loadPlans();
  }

  setTab(tab: 'plans' | 'users'): void {
    this.tab.set(tab);
    this.error.set(null);
    this.success.set(null);
    if (tab === 'plans') this.loadPlans();
    else this.loadSubs();
  }

  loadPlans(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getSubscriptionPlans().subscribe({
      next: (rows) => {
        this.plans.set(Array.isArray(rows) ? rows : []);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load plans');
        this.loading.set(false);
      },
    });
  }

  private resetBenefits(rows: SubscriptionPlanBenefit[] | null | undefined): void {
    this.benefits.clear();
    (rows || []).forEach((b) => this.benefits.push(this.createBenefitGroup(b)));
  }

  editPlan(plan: SubscriptionPlan): void {
    this.editingPlanId.set(plan.id);
    this.planForm.patchValue({
      name: plan.name || '',
      description: plan.description || '',
      tagline: plan.tagline || '',
      card_label: plan.card_label || 'MEMBERSHIP',
      card_title: plan.card_title || plan.name || '',
      card_image_url: plan.card_image_url || '',
      price: Number(plan.price || 0),
      currency: plan.currency || 'AUD',
      currency_symbol: plan.currency_symbol || '$',
      billing_interval: plan.billing_interval || '',
      billing_label: plan.billing_label || '/ month',
      stripe_product_id: plan.stripe_product_id || '',
      stripe_price_id: plan.stripe_price_id || '',
      reward_multiplier: Number(plan.reward_multiplier || 1),
      rent_points: plan.rent_points != null ? Number(plan.rent_points) : null,
      member_offers: !!plan.member_offers,
      ai_assistant: !!plan.ai_assistant,
      smart_insights: !!plan.smart_insights,
      priority_support: !!plan.priority_support,
      concierge_support: !!plan.concierge_support,
      airport_lounge_access: !!plan.airport_lounge_access,
      travel_perks: !!plan.travel_perks,
      comparison_link_text: plan.comparison_link_text || 'Compare all benefits',
      primary_button_text: plan.primary_button_text || 'Continue',
      is_active: !!plan.is_active,
      sort_order: Number(plan.sort_order || 0),
    });
    this.resetBenefits(plan.benefits || []);
    this.success.set(null);
    this.error.set(null);
  }

  addBenefit(): void {
    this.benefits.push(
      this.createBenefitGroup({
        sort_order: this.benefits.length + 1,
        is_clickable: true,
      })
    );
  }

  removeBenefit(index: number): void {
    this.benefits.removeAt(index);
  }

  cancelEdit(): void {
    this.editingPlanId.set(null);
    this.benefits.clear();
  }

  savePlan(): void {
    const id = this.editingPlanId();
    if (!id || this.planForm.invalid || this.saving()) {
      this.planForm.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    const v = this.planForm.getRawValue();
    const benefits: SubscriptionPlanBenefit[] = v.benefits
      .filter((b) => String(b.title || '').trim())
      .map((b, i) => ({
        key: String(b.key || '').trim() || null,
        icon_url: String(b.icon_url || '').trim() || null,
        title: String(b.title).trim(),
        description: String(b.description || '').trim() || null,
        is_clickable: !!b.is_clickable,
        detail_url: String(b.detail_url || '').trim() || null,
        sort_order: Number(b.sort_order || i + 1),
      }));

    this.api
      .updateSubscriptionPlan(id, {
        name: v.name,
        description: v.description || null,
        tagline: v.tagline || null,
        card_label: v.card_label || 'MEMBERSHIP',
        card_title: v.card_title || v.name,
        card_image_url: v.card_image_url || null,
        price: v.price,
        currency: v.currency,
        currency_symbol: v.currency_symbol || '$',
        billing_interval: v.billing_interval || null,
        billing_label: v.billing_label || '/ month',
        stripe_product_id: v.stripe_product_id || null,
        stripe_price_id: v.stripe_price_id || null,
        reward_multiplier: v.reward_multiplier,
        rent_points: v.rent_points != null ? Number(v.rent_points) : null,
        member_offers: v.member_offers,
        ai_assistant: v.ai_assistant,
        smart_insights: v.smart_insights,
        priority_support: v.priority_support,
        concierge_support: v.concierge_support,
        airport_lounge_access: v.airport_lounge_access,
        travel_perks: v.travel_perks,
        benefits,
        comparison_link_text: v.comparison_link_text || 'Compare all benefits',
        primary_button_text: v.primary_button_text || 'Continue',
        is_active: v.is_active,
        sort_order: v.sort_order,
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.editingPlanId.set(null);
          this.benefits.clear();
          this.success.set('Plan updated');
          this.loadPlans();
        },
        error: (err: { message?: string }) => {
          this.saving.set(false);
          this.error.set(err?.message || 'Failed to update plan');
        },
      });
  }

  loadSubs(page = 1): void {
    this.loading.set(true);
    this.error.set(null);
    this.page.set(page);
    this.api.getSubscriptions(page, this.limit).subscribe({
      next: (data) => {
        this.subs.set(data?.rows || []);
        this.total.set(data?.total || 0);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load subscriptions');
        this.loading.set(false);
      },
    });
  }

  openGrant(): void {
    this.showGrant.set(true);
    this.success.set(null);
    this.error.set(null);
  }

  cancelGrant(): void {
    this.showGrant.set(false);
  }

  grant(): void {
    if (this.grantForm.invalid || this.saving()) {
      this.grantForm.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    const v = this.grantForm.getRawValue();
    this.api.grantSubscription({ user_id: Number(v.user_id), plan_key: v.plan_key }).subscribe({
      next: () => {
        this.saving.set(false);
        this.showGrant.set(false);
        this.success.set('Subscription granted');
        this.loadSubs(this.page());
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to grant subscription');
      },
    });
  }

  cancelSub(row: UserSubscription, immediate = false): void {
    if (this.saving() || !row.id) return;
    this.saving.set(true);
    this.api.cancelSubscription({ subscription_id: row.id, immediate }).subscribe({
      next: () => {
        this.saving.set(false);
        this.success.set(immediate ? 'Subscription ended' : 'Cancellation scheduled at period end');
        this.loadSubs(this.page());
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to cancel subscription');
      },
    });
  }

  deleteSub(row: UserSubscription): void {
    if (this.saving() || !row.id) return;
    if (!confirm(`Delete subscription #${row.id} and revert user to Essential?`)) return;
    this.saving.set(true);
    this.api.deleteSubscription(row.id).subscribe({
      next: () => {
        this.saving.set(false);
        this.success.set('Subscription deleted');
        this.loadSubs(this.page());
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to delete subscription');
      },
    });
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.limit));
  }

  benefitCount(plan: SubscriptionPlan): number {
    return Array.isArray(plan.benefits) ? plan.benefits.length : 0;
  }
}
